/**
 * QuantityBadge Component
 * Displays quantity with color-coded stock status indicators
 */

import React from 'react';
import { AlertCircle, CheckCircle2, TrendingDown } from 'lucide-react';

interface QuantityBadgeProps {
  /**
   * Total quantity available
   */
  totalQuantity?: number;

  /**
   * Available quantity (not reserved/sold)
   */
  availableQuantity?: number;

  /**
   * Unit of measurement (kg, liters, etc.)
   */
  unit?: string;

  /**
   * Show stock status indicators
   */
  showAlert?: boolean;

  /**
   * Custom className
   */
  className?: string;

  /**
   * Show compact version (single line)
   */
  compact?: boolean;
}

/**
 * QuantityBadge Component
 * Shows quantity status with visual indicators
 *
 * @example
 * <QuantityBadge
 *   totalQuantity={500}
 *   availableQuantity={350}
 *   unit="kg"
 *   showAlert
 * />
 */
export const QuantityBadge: React.FC<QuantityBadgeProps> = ({
  totalQuantity = 0,
  availableQuantity = 0,
  unit = 'kg',
  showAlert = true,
  className = '',
  compact = false,
}) => {
  // Determine stock status - 20 kg is the auto-deactivation threshold
  const isOutOfStock = availableQuantity === 0;
  const isLowStock = availableQuantity > 0 && availableQuantity < 20;
  const isHealthy = availableQuantity >= 20;

  // Determine color based on status
  // Red for qty = 0, Yellow for qty < 20, Green for qty >= 20
  const getBadgeColor = (): string => {
    if (isOutOfStock) return 'bg-red-100 text-red-800 border-red-400';
    if (isLowStock) return 'bg-yellow-100 text-yellow-800 border-yellow-400';
    return 'bg-green-50 text-green-700 border-green-200';
  };

  // Get status icon
  const getStatusIcon = (): React.ReactNode => {
    if (isOutOfStock) {
      return <AlertCircle className="w-4 h-4" />;
    }
    if (isLowStock) {
      return <TrendingDown className="w-4 h-4" />;
    }
    return <CheckCircle2 className="w-4 h-4" />;
  };

  // Get status label
  const getStatusLabel = (): string => {
    if (isOutOfStock) return 'Out of Stock';
    if (isLowStock) return 'Low Stock';
    return 'In Stock';
  };

  // Compact version (single line)
  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-semibold ${getBadgeColor()} ${className}`}
      >
        {showAlert && getStatusIcon()}
        <span>
          {availableQuantity} {unit} available
        </span>
      </div>
    );
  }

  // Full version (multiple lines)
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Quantity info */}
      <div className="text-xs text-gray-600 space-y-1">
        <div className="flex justify-between items-center">
          <span>Total:</span>
          <span className="font-semibold text-gray-900">
            {totalQuantity} {unit}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span>Available:</span>
          <span className="font-semibold text-gray-900">
            {availableQuantity} {unit}
          </span>
        </div>
      </div>

      {/* Status badge */}
      {showAlert && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-md border ${getBadgeColor()} text-sm font-medium`}
        >
          {getStatusIcon()}
          <span>{getStatusLabel()}</span>
        </div>
      )}

      {/* Stock percentage bar (if not out of stock) */}
      {!isOutOfStock && totalQuantity > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              isLowStock ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{
              width: `${Math.min((availableQuantity / totalQuantity) * 100, 100)}%`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default QuantityBadge;
